import { AxiosError } from 'axios';
import apiClient from './index';

export type EdgeAgentRead = {
  id: string;
  name: string;
  status: string;
  hostname?: string | null;
  os_name?: string | null;
  os_version?: string | null;
  arch?: string | null;
  agent_version?: string | null;
  cpu_percent?: number | null;
  memory_percent?: number | null;
  disk_percent?: number | null;
  load1?: number | null;
  uptime_sec?: number | null;
  started_at?: string | null;
  last_seen_at?: string | null;
  enrolled_at?: string | null;
  revoked_at?: string | null;
  serial_ports: string[];
  credential_prefix?: string | null;
  config_revision: number;
  created_at: string;
  online: boolean;
  pending_commands?: EdgeAgentCommand[];
  last_events?: EdgeAgentEvent[];
  last_command_results?: EdgeAgentCommandResult[];
  meta?: {
    bus_job?: BusJobProgress | null;
    current_command?: CurrentCommandState | null;
    reading_collection?: ReadingCollectionState | null;
    reading_collection_config?: ReadingCollectionSettings | null;
    [key: string]: unknown;
  } | null;
};

export type ReadingCollectionSettings = {
  enabled: boolean;
  interval_sec: number;
  gateway_parallelism?: number;
  request_delay_ms?: number;
  response_timeout_ms?: number;
  retry_count?: number;
  retry_delay_ms?: number;
  retry_jitter_percent?: number;
  adaptive_timeout?: boolean;
  circuit_failures?: number;
  circuit_cooldown_sec?: number;
};

export type CurrentCommandState = {
  id: string;
  type: EdgeAgentCommandType | string;
  automatic?: boolean;
  started_at?: string;
};

export type ReadingCollectionEndpoint = {
  endpoint_id?: string;
  gateway_name?: string;
  access?: string;
  link_ok?: boolean;
  link_message?: string;
  total?: number;
  identified?: number;
  readings?: number;
  no_reply?: number;
  mismatch?: number;
  alive_unknown?: number;
  status?: 'ok' | 'partial' | 'no_devices' | 'link_down' | 'mismatch' | 'without_readings';
  advice?: string;
  reason_counts?: Array<{ message?: string; count?: number }>;
  failure_samples?: Array<{
    device_id?: string;
    serial?: string;
    bus_address?: string;
    status?: string;
    message?: string;
  }>;
};

export type ReadingCollectionState = ReadingCollectionSettings & {
  running?: boolean;
  pending?: number;
  last_started_at?: string;
  last_finished_at?: string;
  next_run_at?: string;
  next_run_in_sec?: number;
  devices_total?: number;
  identified?: number;
  readings?: number;
  no_reply?: number;
  mismatch?: number;
  alive_unknown?: number;
  endpoints_total?: number;
  endpoints_ok?: number;
  configured_endpoints_total?: number;
  distribution_warnings?: string[];
  profile_counts?: Record<string, number>;
  endpoints?: ReadingCollectionEndpoint[];
};

export type BusFoundBrief = {
  bus_address?: string;
  serial?: string;
  profile?: string;
};

export type BusEndpointProgress = {
  endpoint_id?: string;
  gateway_name?: string;
  gateway_type?: string;
  access?: string;
  line_label?: string;
  phase?: string;
  addr?: number;
  addr_total?: number;
  found_count?: number;
  found?: BusFoundBrief[];
  status?: string;
  message?: string;
};

export type BusJobProgress = {
  command_id?: string;
  type?: string;
  active?: boolean;
  summary?: string;
  endpoint_index?: number;
  endpoint_total?: number;
  endpoints?: BusEndpointProgress[];
};

export type EdgeAgentEvent = {
  ts?: string | null;
  level?: string | null;
  message: string;
};

export type EdgeAgentCommand = {
  id: string;
  type: string;
  created_at?: string | null;
  data?: Record<string, unknown> | null;
};

export type EdgeAgentCommandResult = {
  id: string;
  type?: string | null;
  ok: boolean;
  message?: string | null;
  data?: Record<string, unknown> | null;
  finished_at?: string | null;
};

export type EdgeAgentCommandType = 'ping' | 'snapshot' | 'ports' | 'scan' | 'scan_line' | 'collect_readings' | 'discover' | 'search_device' | 'cancel' | 'reset';

export type EdgeAgentEndpoint = {
  id: string;
  kind: 'com' | 'tcp' | string;
  com_port?: string | null;
  host?: string | null;
  tcp_port?: number | null;
  gateway_id: string;
  gateway_name: string;
  building_name?: string | null;
  line_id?: string | null;
  line_number?: string | null;
  line_name?: string | null;
  transport_type?: string | null;
  device_count: number;
  monitoring_mode?: string | null;
  gateway_status?: string | null;
  last_ping_at?: string | null;
  reachability?: string | null;
};

export type EdgeAgentWiringLine = {
  id: string;
  line_number: string;
  name?: string | null;
  transport_type: string;
  device_count: number;
};

export type EdgeAgentWiringGateway = {
  id: string;
  name: string;
  host?: string | null;
  building_name?: string | null;
  lines: EdgeAgentWiringLine[];
};

export type EdgeAgentEndpointCreate = {
  kind?: 'com' | 'tcp';
  com_port?: string;
  host?: string;
  tcp_port?: number;
  gateway_id: string;
  line_id?: string | null;
  /** Один IP, линии RS485 1–8 → TCP 4000–4007 (Пульсар). */
  profile?: 'pulsar_tcp8';
};

export type EdgeAgentUspdCreate = {
  name: string;
  building_id?: string | null;
  kind: 'com' | 'tcp';
  com_port?: string;
  host?: string;
  tcp_port?: number;
  line_id?: string | null;
  profile?: 'single' | 'pulsar_tcp8';
};

export type EdgeAgentEnrollTokenCreated = {
  agent_id: string;
  agent_name: string;
  token: string;
  expires_at: string;
  install_windows: string;
  install_linux: string;
};

export const edgeAgentsApi = {
  async list(): Promise<EdgeAgentRead[]> {
    const res = await apiClient.get<EdgeAgentRead[]>('/edge-agents/');
    return res.data;
  },
  async create(name: string): Promise<EdgeAgentRead> {
    const res = await apiClient.post<EdgeAgentRead>('/edge-agents/', { name });
    return res.data;
  },
  async issueEnrollToken(id: string): Promise<EdgeAgentEnrollTokenCreated> {
    const res = await apiClient.post<EdgeAgentEnrollTokenCreated>(`/edge-agents/${id}/enroll-token`);
    return res.data;
  },
  async revoke(id: string): Promise<EdgeAgentRead> {
    const res = await apiClient.post<EdgeAgentRead>(`/edge-agents/${id}/revoke`);
    return res.data;
  },
  async enqueueCommand(id: string, type: EdgeAgentCommandType, data?: Record<string, unknown>): Promise<EdgeAgentRead> {
    const res = await apiClient.post<EdgeAgentRead>(`/edge-agents/${id}/commands`, { type, data });
    return res.data;
  },
  async resetTasks(id: string): Promise<EdgeAgentRead> {
    const res = await apiClient.post<EdgeAgentRead>(`/edge-agents/${id}/reset-tasks`);
    return res.data;
  },
  async updateReadingCollection(id: string, payload: ReadingCollectionSettings): Promise<EdgeAgentRead> {
    const res = await apiClient.patch<EdgeAgentRead>(`/edge-agents/${id}/reading-collection`, payload);
    return res.data;
  },
  async wiringOptions(): Promise<EdgeAgentWiringGateway[]> {
    const res = await apiClient.get<EdgeAgentWiringGateway[]>('/edge-agents/wiring-options');
    return res.data;
  },
  async listEndpoints(id: string): Promise<EdgeAgentEndpoint[]> {
    const res = await apiClient.get<EdgeAgentEndpoint[]>(`/edge-agents/${id}/endpoints`);
    return res.data;
  },
  async createEndpoint(id: string, payload: EdgeAgentEndpointCreate): Promise<EdgeAgentRead> {
    const res = await apiClient.post<EdgeAgentRead>(`/edge-agents/${id}/endpoints`, payload);
    return res.data;
  },
  async createUspd(id: string, payload: EdgeAgentUspdCreate): Promise<EdgeAgentRead> {
    const res = await apiClient.post<EdgeAgentRead>(`/edge-agents/${id}/uspd`, payload);
    return res.data;
  },
  async deleteEndpoint(id: string, endpointId: string): Promise<EdgeAgentRead> {
    const res = await apiClient.delete<EdgeAgentRead>(`/edge-agents/${id}/endpoints/${endpointId}`);
    return res.data;
  },
  async download(kind: 'windows' | 'linux'): Promise<void> {
    const filename = kind === 'windows' ? 'BillingService-Agent.exe' : 'billingservice-agent';
    try {
      const res = await apiClient.get(`/edge-agent/download/${kind}`, {
        responseType: 'blob',
        headers: { Accept: 'application/octet-stream' },
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err instanceof AxiosError && typeof Blob !== 'undefined' && err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const parsed = JSON.parse(text) as { detail?: unknown };
          if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
            err.response.data = { detail: parsed.detail };
            err.message = parsed.detail;
          }
        } catch {
          // keep the original axios error
        }
      }
      throw err;
    }
  },
};
